import React, {
    ChangeEvent,
    ComponentType,
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef
} from 'react';
import { ReferenceInput } from 'sr-types/lib/person/v1/graphql';
import { BasicInfoInputExtended } from '../../../person/detailsPage/detailsPageUtilities/personBasicInformation';
import { documentExtensions, filesExtensions } from '../../attachments/attachmentsApi';
import { UserContext } from '../../auth/UserContext';
import { Reference } from '../../reference/reference';
import upload, { UPLOAD_URL } from '../../utils/upload';

type FileUploaderProps = {
    UploadIconComponent: ComponentType<any>;
    setIsUploading: Dispatch<SetStateAction<boolean>>;
    entityId: string;
    selectedFile?: File | null;
    setSelectedFile?: Dispatch<SetStateAction<File | null>>;
    setEntityReference?: Dispatch<SetStateAction<Reference>>;
    FileRenderer?: ComponentType<any>;
    fileReference?: ReferenceInput;
    setState?: Dispatch<SetStateAction<BasicInfoInputExtended>>;
    multiple?: boolean;
    onUploadComplete?: (selectedFiles, references: Reference[] | Reference) => void;
    acceptedFiles?: string;
    hideUploadResumeIcon?: boolean;
    disabled?: boolean;
    autoOpenFilePickerTrigger?: number;
    /** Called after an auto-open request is consumed, whether it opens the picker or is blocked while disabled. */
    onAutoOpenFilePickerHandled?: () => void;
};

export default function FileUploader({
    selectedFile,
    setSelectedFile,
    UploadIconComponent,
    setEntityReference,
    FileRenderer,
    setIsUploading,
    fileReference,
    setState,
    entityId,
    multiple = false,
    onUploadComplete,
    acceptedFiles = `${filesExtensions[0]},` + documentExtensions.join(','),
    hideUploadResumeIcon = false,
    disabled = false,
    autoOpenFilePickerTrigger,
    onAutoOpenFilePickerHandled
}: FileUploaderProps) {
    const { activeOrganizationAccount } = useContext(UserContext);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const lastAutoOpenFilePickerTriggerRef = useRef<number | undefined>(undefined);

    const handleFileUpload = useCallback(() => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    }, [disabled]);

    useEffect(() => {
        if (autoOpenFilePickerTrigger === undefined) {
            lastAutoOpenFilePickerTriggerRef.current = undefined;
            return;
        }

        if (lastAutoOpenFilePickerTriggerRef.current === autoOpenFilePickerTrigger) {
            return;
        }

        lastAutoOpenFilePickerTriggerRef.current = autoOpenFilePickerTrigger;

        if (disabled) {
            onAutoOpenFilePickerHandled?.();
            return;
        }

        const fileInput = fileInputRef.current;
        if (!fileInput) {
            onAutoOpenFilePickerHandled?.();
            return;
        }

        fileInput.click();
        onAutoOpenFilePickerHandled?.();
    }, [autoOpenFilePickerTrigger, disabled, onAutoOpenFilePickerHandled]);

    const onUpdateFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length) {
            setIsUploading(true);
            if (multiple) {
                const fileArray = Array.from(files);
                setSelectedFile?.(fileArray as any);
                const references = [];
                for (const file of fileArray) {
                    const { data } = await upload(activeOrganizationAccount, UPLOAD_URL, file);
                    references.push(data?.reference);
                }
                setEntityReference?.(references as any);
                onUploadComplete?.(fileArray, references);
            } else {
                const file = files[0];
                setSelectedFile?.(file);
                const { data } = await upload(activeOrganizationAccount, UPLOAD_URL, file);
                setEntityReference?.(data?.reference);
                onUploadComplete?.(file, data?.reference);
            }

            setIsUploading(false);
        }
        event.target.value = null;
    };

    return (
        <>
            {fileReference ? (
                <FileRenderer selectedFile={selectedFile} entityReference={fileReference} setState={setState} />
            ) : (
                !hideUploadResumeIcon && (
                    <>
                        <UploadIconComponent onClick={handleFileUpload} disabled={disabled} />
                        <input
                            ref={fileInputRef}
                            multiple={multiple}
                            type="file"
                            style={{ display: 'none' }}
                            onChange={onUpdateFile}
                            accept={acceptedFiles}
                        />
                    </>
                )
            )}
        </>
    );
}
